import store from '@/store';
import { isPlanActive, syncCurrentPlanFromActiveOrder } from '@/utils/plan-utils';

export const isEmbeddedNavigation = (from) => {
  const referrer = document.referrer;
  return (from?.name && from?.name?.includes('embedded')) || (referrer && referrer?.includes('/int/'));
};

export const allowNextGuard = (_to, _from, next) => next();

export const createEmbeddedRedirectGuard = (embeddedRouteName, { includeQuery = false } = {}) => {
  return (to, from, next) => {
    if (isEmbeddedNavigation(from)) {
      next({
        name: embeddedRouteName,
        params: { ...to.params },
        ...(includeQuery ? { query: { ...to.query } } : {}),
      });
      return;
    }
    next();
  };
};

export const createThankYouGuard = () => {
  return (to, from, next) => {
    if (isEmbeddedNavigation(from)) {
      next({ name: 'embedded-thanks', query: { ...to.query } });
      return;
    }
    next();
  };
};

export const createScheduleStartGuard = () => {
  return (to, from, next) => {
    if (window.location.hostname.includes('avus')) {
      next({ name: 'error', params: { code: 404 } });
      return;
    }
    if (isEmbeddedNavigation(from)) {
      next({ name: 'embedded-start', query: { ...to.query } });
      return;
    }
    next();
  };
};

export const createFeatureGuard = (getterPath, redirectName = 'schedule-start') => {
  return (_to, _from, next) => {
    if (!store.getters[getterPath]) {
      next({ name: redirectName });
      return;
    }
    next();
  };
};

export const chainRouteGuards = (...guards) => {
  return async (to, from, next) => {
    try {
      for (const guard of guards) {
        const step = await new Promise((resolve, reject) => {
          const proceed = (value) => resolve({ redirected: value !== undefined, value });
          try {
            const result = guard(to, from, proceed);
            if (result && typeof result.then === 'function') {
              result.catch(reject);
            }
          } catch (error) {
            reject(error);
          }
        });

        if (step.redirected) {
          next(step.value);
          return;
        }
      }
      next();
    } catch {
      next({ name: 'backoffice-home' });
    }
  };
};

export const createPaidPlanGuard = () => {
  return async (_to, _from, next) => {
    try {
      const { subscription } = await syncCurrentPlanFromActiveOrder({
        getSubscription: (params) => store.dispatch('plans-subscription/getSubscription', params),
        getAllPlans: (params) => store.dispatch('tmed-plan/getAllPlans', params),
        setCurrentSubscription: (subscription) =>
          store.dispatch('plans-subscription/setCurrentSubscription', subscription),
        setCurrentPlan: (plan) => store.dispatch('tmed-plan/setCurrentPlan', plan),
        isPrivateContext: true,
        onUnpaid: () => next({ name: 'backoffice-home' }),
      });

      if (!isPlanActive(subscription)) {
        return;
      }

      next();
    } catch {
      next({ name: 'backoffice-home' });
    }
  };
};
